import { fmtNum } from "@/lib/format";

const Counter = ({ label, value, tone, testId, sub }) => (
  <div className={`rounded-xl border p-4 ${tone}`}>
    <p className="text-[11px] font-semibold uppercase tracking-wider opacity-80">{label}</p>
    <p className="font-mono-num mt-1 text-2xl font-semibold" data-testid={testId}>{value}</p>
    {sub && <p className="mt-0.5 text-[11px] opacity-80" data-testid={`${testId}-sub`}>{sub}</p>}
  </div>
);

const ContractTable = ({ s, contracts }) => (
  <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
    <table className="w-full text-sm" data-testid="fleet-contract-table">
      <thead className="border-b bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500 [&_th]:whitespace-nowrap">
        <tr><th className="px-3 py-2">Contratto</th><th className="px-3 py-2 text-right">Disponibili</th><th className="px-3 py-2 text-right">Non disp.</th>
          <th className="px-3 py-2 text-right">Rescue</th><th className="px-3 py-2 text-right">Trasferiti</th><th className="px-3 py-2 text-right">Totale flotta</th><th className="px-3 py-2 text-right">% fermo</th></tr>
      </thead>
      <tbody className="divide-y">
        {Object.entries(contracts).map(([k, label]) => {
          const c = s.by_contract[k];
          return (
            <tr key={k} data-testid={`fleet-contract-row-${k}`}>
              <td className="px-3 py-2 font-medium">{label}</td>
              <td className="font-mono-num px-3 py-2 text-right text-emerald-700">{c.disponibili}</td>
              <td className="font-mono-num px-3 py-2 text-right text-rose-700">{c.non_disponibili}</td>
              <td className="font-mono-num px-3 py-2 text-right">{c.rescue}</td>
              <td className="font-mono-num px-3 py-2 text-right">{c.trasferiti}</td>
              <td className="font-mono-num px-3 py-2 text-right font-semibold">{c.totale_flotta}</td>
              <td className="font-mono-num px-3 py-2 text-right">{k === "LT" ? `${fmtNum(c.fermo_pct, 2)}%` : ""}</td>
            </tr>
          );
        })}
        <tr className="bg-slate-50 font-semibold">
          <td className="px-3 py-2">Totale</td>
          <td className="font-mono-num px-3 py-2 text-right">{s.disponibili}</td><td className="font-mono-num px-3 py-2 text-right">{s.non_disponibili}</td>
          <td className="font-mono-num px-3 py-2 text-right">{s.rescue}</td><td className="font-mono-num px-3 py-2 text-right">{s.trasferiti}</td>
          <td className="font-mono-num px-3 py-2 text-right">{s.totale_flotta}</td><td />
        </tr>
      </tbody>
    </table>
  </div>
);

const ListCard = ({ title, items, testId }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid={testId}>
    <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">{title}</p>
    {items.length === 0 ? <p className="text-sm text-slate-400">Nessuno</p> : (
      <ul className="space-y-1.5 text-sm">
        {items.map(([k, v]) => <li key={k} className="flex justify-between gap-2"><span className="truncate">{k}</span><span className="font-mono-num font-semibold">{v}</span></li>)}
      </ul>
    )}
  </div>
);

export const FleetSummary = ({ s, contracts }) => (
  <div className="mb-6 space-y-4" data-testid="fleet-summary">
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 xl:grid-cols-8">
      <Counter label="Disponibili" value={s.disponibili} tone="border-emerald-200 bg-emerald-50 text-emerald-800" testId="fleet-disponibili" />
      <Counter label="Non disponibili" value={s.non_disponibili} tone="border-rose-200 bg-rose-50 text-rose-800" testId="fleet-non-disponibili" />
      <Counter label="Trasferiti altre sedi" value={s.trasferiti} tone="border-orange-200 bg-orange-50 text-orange-800" testId="fleet-trasferiti" />
      <Counter label="Rescue" value={s.rescue} tone="border-sky-200 bg-sky-50 text-sky-800" testId="fleet-rescue" />
      <Counter label="Totale non disponibili" value={s.totale_non_disponibili} tone="border-slate-200 bg-white text-slate-900" testId="fleet-tot-non-disp" />
      <Counter label="Totale flotta" value={s.totale_flotta} sub={s.ricevuti ? `di cui ${s.ricevuti} ricevuti da altre sedi` : null} tone="border-blue-200 bg-blue-50 text-blue-800" testId="fleet-totale" />
      <Counter label="Totale incl. trasferiti" value={s.totale_con_trasferiti} tone="border-slate-200 bg-white text-slate-900" testId="fleet-totale-trasf" />
      <Counter label="% fermo lungo termine" value={s.fermo_lt_pct == null ? "—" : `${fmtNum(s.fermo_lt_pct, 2)}%`} tone="border-amber-300 bg-amber-50 text-amber-800" testId="fleet-fermo-pct" />
    </div>
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-12">
      <div className="min-w-0 lg:col-span-7"><ContractTable s={s} contracts={contracts} /></div>
      <div className="grid min-w-0 gap-4 sm:grid-cols-2 lg:col-span-5">
        <ListCard title="Cause di fermo" items={s.by_reason.map((r) => [r.reason, r.count])} testId="fleet-by-reason" />
        <ListCard title="Società (disp. / flotta)" items={s.by_provider.map((p) => [p.provider, `${p.disponibili}/${p.totale_flotta}`])} testId="fleet-by-provider" />
      </div>
    </div>
  </div>
);
